import React, { useCallback, useState, useMemo, useEffect } from 'react';
import axios from "axios";
import dagre from 'dagre';

import { Handle, Position } from 'reactflow';
import { Fetchexecutequery } from "../../../services/process/BRS1/brsService7";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  MarkerType,
  ConnectionLineType,
} from 'reactflow';
import 'reactflow/dist/style.css';

const NoHandleNode = ({ data }) => (
  <div
    style={{
      padding: 10,
      border: '1px solid #999',
      background: '#fff',
      borderRadius: 6,
      textAlign: 'center',
      cursor: 'pointer',
    }}
  >
    {data.label}
    <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
    <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
  </div>
);

const nodeTypes = {
  nohandle: NoHandleNode,
};

// Dagre layout helper
const getLayoutedElements = (nodes, edges, direction = 'TB') => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 100, height: 50 });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const pos = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: pos.x - 75,
        y: pos.y - 25,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

const ReactFlowOrgEditor = () => {
  const [tenants, setTenants] = useState([]);
  const [groups, setGroups] = useState([]);
  const [entities, setEntities] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [collapsed, setCollapsed] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);
const fetchAllData = async () => {
  try {
    const creds = Fetchexecutequery();

    const [tenantsRes, groupsRes, entitiesRes, divisionsRes] = await Promise.all([
      axios.post(creds[1], {
        query: `SELECT tenants_id as id, name FROM user_management_uat.tenants WHERE is_active = 1;`,
      }, creds[0]),
      axios.post(creds[1], {
        query: `SELECT groups_id as id, name, tenants_id as parentId FROM user_management_uat.groups WHERE is_active = 1;`,
      }, creds[0]),
      axios.post(creds[1], {
        query: `SELECT entity_id as id, name, groups_id as parentId FROM user_management_uat.entities WHERE is_active = 1;`,
      }, creds[0]),
      axios.post(creds[1], {
        query: `SELECT division_id as id, name, entity_id as parentId FROM user_management_uat.divisions WHERE is_active = 1;`,
      }, creds[0]),
    ]);

    const tenantData = tenantsRes.data.map((item) => ({
      id: `tenant-${item.id}`,
      label: item.name,
      parentId: null,
      level: 'tenant',
    }));

    const groupData = groupsRes.data.map((item) => ({
      id: `group-${item.id}`,
      label: item.name,
      parentId: `tenant-${item.parentId}`,
      level: 'group',
    }));

    const entityData = entitiesRes.data.map((item) => ({
      id: `entity-${item.id}`,
      label: item.name,
      parentId: `group-${item.parentId}`,
      level: 'entity',
    }));

    const divisionData = divisionsRes.data.map((item) => ({
      id: `division-${item.id}`,
      label: item.name,
      parentId: `entity-${item.parentId}`,
      level: 'division',
    }));

    const all = [...tenantData, ...groupData, ...entityData, ...divisionData];

    const nodesBuilt = all.map((item) => ({
      id: item.id,
      data: { label: item.label },
      position: { x: 0, y: 0 },
      type: 'nohandle',
      parentId: item.parentId,
      level: item.level,
      style: {
        overflow: 'visible',
        whiteSpace: 'nowrap',
        textAlign: 'center',
        padding: 10,
        borderRadius: 6,
      },
    }));

    const edgesBuilt = all
      .filter((item) => item.parentId)
      .map((item) => ({
        id: `e-${item.parentId}-${item.id}`,
        source: item.parentId,
        target: item.id,
        type: 'smoothstep',
        markerEnd: { type: MarkerType.Arrow },
        animated: true,
      }));

    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(nodesBuilt, edgesBuilt);
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);

  } catch (error) {
    console.error("Error fetching data:", error);
    setError("Error fetching data");
  }
};

 const computeHiddenNodes = (collapsedIds) => {
  const allData = nodes; // ?? Use actual rendered node data (with correct IDs)
  const childMap = new Map();

  allData.forEach((item) => {
    if (item.parentId) {
      if (!childMap.has(item.parentId)) childMap.set(item.parentId, []);
      childMap.get(item.parentId).push(item.id);
    }
  });

  const hidden = new Set();
  const stack = [...collapsedIds];

  while (stack.length) {
    const current = stack.pop();
    const children = childMap.get(current) || [];
    children.forEach((child) => {
      hidden.add(child);
      stack.push(child);
    });
  }

  return hidden;
};

const hiddenIds = useMemo(() => computeHiddenNodes(collapsed), [collapsed, nodes]);
const visibleNodes = useMemo(() => nodes.filter((n) => !hiddenIds.has(n.id)), [nodes, hiddenIds]);
const visibleEdges = useMemo(() => edges.filter((e) => !hiddenIds.has(e.source) && !hiddenIds.has(e.target)), [edges, hiddenIds]);

  const onNodeClick = useCallback((_, node) => {
    setCollapsed((prev) =>
      prev.includes(node.id) ? prev.filter((id) => id !== node.id) : [...prev, node.id]
    );
  }, []);

  return (
    <div style={{ width: '100%', height: '100vh', position: 'relative' }}>
      <h2 style={{ padding: '10px' }}>Org Hierarchy (Auto Layout + Collapse)</h2>
      {error && <div style={{ color: 'red', paddingLeft: 10 }}>{error}</div>}
      <ReactFlow
        nodes={visibleNodes}
        edges={visibleEdges}
        nodeTypes={nodeTypes}
        fitView
        onNodeClick={onNodeClick}
        nodesConnectable={false}
        elementsSelectable
        connectionLineType={ConnectionLineType.SmoothStep}
        connectOnClick={false}
      >
        <Controls showZoom showFitView showInteractive={false} />
        <MiniMap zoomable pannable />
        <Background variant="dots" gap={10} size={1} />
      </ReactFlow>
    </div>
  );
};

export default ReactFlowOrgEditor;
